import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Avatar, AvatarFallback } from "@v2/components/ui/avatar";
import { Button } from "@v2/components/ui/button";
import { Switch } from "@v2/components/ui/switch";
import { Input } from "@v2/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { playerRoleLabel, rolePriority, roleSectionLabel } from "../../lib/labels";
import { usePermissions } from "../../lib/permissions";
import type { Match, MatchPlayerStat, Player } from "../../types";
import { EaImportButton } from "./EaImportButton";
import { CompetitionPicker } from "../../components/competition-picker";

export function MatchOpenPanel({
  match,
  stats,
  assignedGoals,
  startersCount,
  matchDayPresentIds,
  rosterPlayers,
  presentPlayers,
  isMarkingStarters,
  isClearingStarters,
  isUpdatingScore,
  isUpdatingMatch,
  onUpsertStat,
  onMarkStarters,
  onClearStarters,
  onUpdateScore,
  onUpdateMatch,
}: {
  match: Match;
  stats: MatchPlayerStat[];
  assignedGoals: number;
  startersCount: number;
  matchDayPresentIds: string[];
  rosterPlayers: Player[];
  presentPlayers: Player[];
  isMarkingStarters: boolean;
  isClearingStarters: boolean;
  isUpdatingScore: boolean;
  isUpdatingMatch: boolean;
  onUpsertStat: (payload: Omit<MatchPlayerStat, "id">) => void;
  onMarkStarters: () => void;
  onClearStarters: () => void;
  onUpdateScore: (ourScore: number, opponentScore: number) => void;
  onUpdateMatch: (payload: Partial<Match>) => void;
}) {
  const { t } = useTranslation("sessions");
  const { canManage } = usePermissions();
  const queryClientLocal = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [showRest, setShowRest] = useState(false);
  const statByPlayer = new Map(stats.map((s) => [s.player_id, s]));

  // Callups per la sessione contenitore del match
  const callupsLocalQuery = useQuery({
    queryKey: ["session-callups-match", match.session_id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("session_callups")
        .select("id, player_id, status")
        .eq("session_id", match.session_id);
      if (error) throw error;
      return (data || []) as Array<{ id: string; player_id: string; status: "draft" | "confirmed" | "removed" }>;
    },
    enabled: !!match.session_id,
  });
  const callupByPlayerLocal = new Map(
    (callupsLocalQuery.data || []).map((c) => [c.player_id, c])
  );

  const toggleCallup = useMutation({
    mutationFn: async ({ playerId, confirm }: { playerId: string; confirm: boolean }) => {
      const { error } = await supabaseV2.from("session_callups").upsert(
        {
          session_id: match.session_id,
          player_id: playerId,
          status: confirm ? "confirmed" : "draft",
          confirmed_at: confirm ? new Date().toISOString() : null,
        },
        { onConflict: "session_id,player_id" }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClientLocal.invalidateQueries({ queryKey: ["session-callups-match", match.session_id] });
      queryClientLocal.invalidateQueries({ queryKey: ["session-callups"] });
    },
  });
  const sortByRole = (a: Player, b: Player) => {
    const d = rolePriority(a.role_code) - rolePriority(b.role_code);
    if (d !== 0) return d;
    return a.display_name.localeCompare(b.display_name);
  };
  const presentRows = presentPlayers.slice().sort(sortByRole);
  const restRows = rosterPlayers
    .filter((p) => !matchDayPresentIds.includes(p.id))
    .filter((p) => statByPlayer.has(p.id))
    .sort(sortByRole);
  const presentByRole = presentRows.reduce((acc, p) => {
    const key = rolePriority(p.role_code);
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key)!.push(p);
    return acc;
  }, new Map<number, Player[]>());
  const orderedRoleSections = [...presentByRole.entries()].sort(([a], [b]) => a - b);
  const matchPresenceCovered = presentRows.length;
  // Gol "coperti" = assegnati a un marcatore + autogol avversari.
  const coveredGoals = assignedGoals + (match.opponent_own_goals ?? 0);
  return (
    <div className="border-t">
      <div className="px-2 py-3 flex items-center gap-3 flex-wrap text-[13px]">
        <CompactScoreEditor match={match} isSaving={isUpdatingScore} onSave={onUpdateScore} />
        <OwnGoalsEditor
          value={match.opponent_own_goals ?? 0}
          isSaving={isUpdatingMatch}
          onSave={(next) => onUpdateMatch({ opponent_own_goals: next })}
        />
        {(match.our_score ?? 0) > 0 && (
          <span className={cn(
            "inline-flex items-center gap-1.5 font-medium",
            coveredGoals < (match.our_score ?? 0)
              ? "text-[hsl(var(--warn))]"
              : "text-[hsl(var(--ok))]"
          )}>
            {coveredGoals < (match.our_score ?? 0)
              ? t("matchPanel.toAssign", { count: (match.our_score ?? 0) - coveredGoals })
              : t("matchPanel.goalsAssigned")}
          </span>
        )}
        <span className="text-muted-foreground">
          {t("matchPanel.starters")} <strong className="text-foreground tabular-nums">{startersCount}</strong>/{matchPresenceCovered} {t("matchPanel.presentSuffix")}
        </span>
        {canManage && (
          <label className="inline-flex items-center gap-1.5 text-muted-foreground" title={t("matchPanel.countsForStatsHint")}>
            <Switch
              checked={match.counts_for_stats !== false}
              disabled={isUpdatingMatch}
              onCheckedChange={(checked) => onUpdateMatch({ counts_for_stats: checked })}
            />
            {t("matchPanel.countsForStats")}
          </label>
        )}
        <div className="flex gap-2 w-full lg:w-auto lg:ml-auto flex-wrap">
          <Button
            size="sm"
            variant="outline"
            className="h-11 sm:h-9 text-sm"
            disabled={isMarkingStarters || matchDayPresentIds.length === 0}
            onClick={onMarkStarters}
            title={t("matchPanel.allStartersTitle")}
          >
            <CheckCircle2 className="h-4 w-4" />
            {t("matchPanel.allStarters")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-11 sm:h-9 text-sm"
            disabled={isClearingStarters || startersCount === 0}
            onClick={onClearStarters}
          >
            {t("matchPanel.resetT")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-11 sm:h-9 text-sm"
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? t("matchPanel.closeEdit") : t("matchPanel.openEdit")}
          </Button>
          <EaImportButton match={match} />
        </div>
      </div>
      {editing && (
        <div className="px-2 pb-3">
          <CompactMatchEditor match={match} isSaving={isUpdatingMatch} onSave={onUpdateMatch} />
        </div>
      )}
      {/* Header tabella solo su desktop: su mobile usiamo card stacked con
          label inline negli stepper. */}
      <div className="hidden lg:grid text-[13px] text-[hsl(var(--faint))] px-2 pt-2 pb-1 grid-cols-[28px_minmax(0,1fr)_36px_36px_repeat(4,92px)] gap-2">
        <span />
        <span>{t("matchPanel.calledUpTitle", { count: presentRows.length })}</span>
        <span className="text-center">{t("matchPanel.thCallup")}</span>
        <span className="text-center">{t("matchPanel.thStarter")}</span>
        <span className="text-center">{t("matchPanel.thGoals")}</span>
        <span className="text-center">{t("matchPanel.thAssists")}</span>
        <span className="text-center">{t("matchPanel.thYellow")}</span>
        <span className="text-center">{t("matchPanel.thRed")}</span>
      </div>
      <div className="lg:hidden px-2 pt-2 pb-1 font-semibold">
        {t("matchPanel.calledUpTitle", { count: presentRows.length })}
      </div>
      <div>
        {orderedRoleSections.map(([roleKey, players]) => (
          <div key={roleKey}>
            <div className="px-2 pt-3 pb-1 text-[13px] text-[hsl(var(--faint))]">
              {roleSectionLabel(roleKey)} · {players.length}
            </div>
            <div className="divide-y">
              {players.map((p) => (
                <MatchPlayerStatRow
                  key={p.id}
                  matchId={match.id}
                  player={p}
                  stat={statByPlayer.get(p.id)}
                  onApply={onUpsertStat}
                  isCallup={callupByPlayerLocal.get(p.id)?.status === "confirmed"}
                  onToggleCallup={(confirm) => toggleCallup.mutate({ playerId: p.id, confirm })}
                />
              ))}
            </div>
          </div>
        ))}
        {presentRows.length === 0 && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t("matchPanel.noCallups")}
          </div>
        )}
      </div>
      {restRows.length > 0 && (
        <div className="border-t">
          <button
            type="button"
            onClick={() => setShowRest((v) => !v)}
            className="w-full text-left px-2 min-h-11 text-sm text-muted-foreground hover:text-foreground"
          >
            {showRest ? "− " : "+ "} {t("matchPanel.moreWithStats", { count: restRows.length })}
          </button>
          {showRest && (
            <div className="divide-y">
              {restRows.map((p) => (
                <MatchPlayerStatRow
                  key={p.id}
                  matchId={match.id}
                  player={p}
                  stat={statByPlayer.get(p.id)}
                  onApply={onUpsertStat}
                  isCallup={callupByPlayerLocal.get(p.id)?.status === "confirmed"}
                  onToggleCallup={(confirm) => toggleCallup.mutate({ playerId: p.id, confirm })}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompactScoreEditor({
  match,
  isSaving,
  onSave,
}: {
  match: Match;
  isSaving: boolean;
  onSave: (ourScore: number, opponentScore: number) => void;
}) {
  const { t } = useTranslation("sessions");
  const [ours, setOurs] = useState(String(match.our_score ?? 0));
  const [theirs, setTheirs] = useState(String(match.opponent_score ?? 0));
  useEffect(() => {
    setOurs(String(match.our_score ?? 0));
    setTheirs(String(match.opponent_score ?? 0));
  }, [match.our_score, match.opponent_score]);
  const dirty = Number(ours || 0) !== Number(match.our_score ?? 0) || Number(theirs || 0) !== Number(match.opponent_score ?? 0);
  return (
    <div className="inline-flex items-center gap-2">
      <Input
        value={ours}
        onChange={(e) => setOurs(e.target.value)}
        type="number"
        min="0"
        className="eb-num h-12 w-14 text-center px-1 text-[28px] md:text-[28px]"
      />
      <span className="eb-num text-[28px] text-[hsl(var(--faint))]">-</span>
      <Input
        value={theirs}
        onChange={(e) => setTheirs(e.target.value)}
        type="number"
        min="0"
        className="eb-num h-12 w-14 text-center px-1 text-[28px] md:text-[28px]"
      />
      {dirty && (
        <Button
          size="sm"
          variant="default"
          className="h-11 sm:h-9 text-sm"
          disabled={isSaving}
          onClick={() => onSave(Number(ours || 0), Number(theirs || 0))}
        >
          {t("matchPanel.save")}
        </Button>
      )}
    </div>
  );
}

// Autogol avversari: chiudono il conto dei gol da assegnare senza marcatore.
function OwnGoalsEditor({
  value,
  isSaving,
  onSave,
}: {
  value: number;
  isSaving: boolean;
  onSave: (next: number) => void;
}) {
  const { t } = useTranslation("sessions");
  const [draft, setDraft] = useState(String(value));
  useEffect(() => {
    setDraft(String(value));
  }, [value]);
  const next = Math.max(0, Number(draft || 0));
  const dirty = next !== value;
  return (
    <label className="inline-flex items-center gap-2 text-muted-foreground" title={t("matchPanel.ownGoalsTitle")}>
      <span>{t("matchPanel.ownGoals")}</span>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        type="number"
        min="0"
        className="h-11 w-14 text-center px-1 font-semibold"
      />
      {dirty && (
        <Button size="sm" variant="default" className="h-11 sm:h-9 text-sm" disabled={isSaving} onClick={() => onSave(next)}>
          {t("matchPanel.save")}
        </Button>
      )}
    </label>
  );
}

function CompactMatchEditor({
  match,
  isSaving,
  onSave,
}: {
  match: Match;
  isSaving: boolean;
  onSave: (payload: Partial<Match>) => void;
}) {
  const { t } = useTranslation("sessions");
  // Date in LOCALE (toLocaleDateString sv-SE = YYYY-MM-DD locale), coerente
  // con time (toTimeString = locale). Mescolare UTC (toISOString) e locale
  // produce inconsistenze al confine di mezzanotte.
  const initialDate = match.starts_at ? new Date(match.starts_at).toLocaleDateString("sv-SE") : "";
  const initialTime = match.starts_at ? new Date(match.starts_at).toTimeString().slice(0, 5) : "";
  const [opponent, setOpponent] = useState(match.opponent_name);
  const [competition, setCompetition] = useState(match.competition_name || "");
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [homeAway, setHomeAway] = useState<Match["home_away"]>(match.home_away);
  useEffect(() => {
    setOpponent(match.opponent_name);
    setCompetition(match.competition_name || "");
    setDate(match.starts_at ? new Date(match.starts_at).toLocaleDateString("sv-SE") : "");
    setTime(match.starts_at ? new Date(match.starts_at).toTimeString().slice(0, 5) : "");
    setHomeAway(match.home_away);
  }, [match.id, match.opponent_name, match.competition_name, match.starts_at, match.home_away]);
  return (
    <form
      className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          opponent_name: opponent,
          competition_name: competition || null,
          // BUGFIX: la string `${date}T${time}` senza tz viene interpretata
          // da PostgreSQL timestamptz come UTC, quindi al re-render appariva
          // shiftata di +offset (es. 22:20 → 00:20 in CEST). Usiamo
          // `new Date(...).toISOString()` per convertire l'input locale in
          // UTC prima di inviarlo, cosi' lo storage e il re-render coincidono.
          starts_at: date ? new Date(`${date}T${time || "12:00"}`).toISOString() : null,
          home_away: homeAway,
        });
      }}
    >
      <Input value={opponent} onChange={(e) => setOpponent(e.target.value)} placeholder={t("matchPanel.opponentPh")} list="team-opponents" className="col-span-2 sm:flex-1 sm:min-w-[160px]" />
      <CompetitionPicker teamId={match.team_id} value={competition} onChange={setCompetition} className="col-span-2 sm:flex-1 sm:min-w-[160px]" />
      <Input value={date} onChange={(e) => setDate(e.target.value)} type="date" className="sm:w-[150px]" />
      <Input value={time} onChange={(e) => setTime(e.target.value)} type="time" className="sm:w-[110px]" />
      <Select value={homeAway} onValueChange={(v) => setHomeAway(v as Match["home_away"])}>
        <SelectTrigger className="col-span-2 sm:w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="home">{t("wizard.matches.home")}</SelectItem>
          <SelectItem value="away">{t("wizard.matches.away")}</SelectItem>
          <SelectItem value="neutral">{t("wizard.matches.neutral")}</SelectItem>
        </SelectContent>
      </Select>
      <Button type="submit" size="sm" variant="default" className="col-span-2 h-11" disabled={isSaving}>
        {t("matchPanel.save")}
      </Button>
    </form>
  );
}

function MatchPlayerStatRow({
  matchId,
  player,
  stat,
  onApply,
  isCallup,
  onToggleCallup,
}: {
  matchId: string;
  player: Player;
  stat?: MatchPlayerStat;
  onApply: (next: {
    match_id: string;
    player_id: string;
    goals: number;
    assists: number;
    yellow_cards: number;
    red_cards: number;
    started: boolean;
  }) => void;
  isCallup?: boolean;
  onToggleCallup?: (confirm: boolean) => void;
}) {
  const { t } = useTranslation("sessions");
  const goals = stat?.goals || 0;
  const assists = stat?.assists || 0;
  const yellow = stat?.yellow_cards || 0;
  const red = stat?.red_cards || 0;
  const started = !!stat?.started;
  const apply = (changes: Partial<{ goals: number; assists: number; yellow_cards: number; red_cards: number; started: boolean }>) => {
    onApply({
      match_id: matchId,
      player_id: player.id,
      goals: changes.goals ?? goals,
      assists: changes.assists ?? assists,
      yellow_cards: changes.yellow_cards ?? yellow,
      red_cards: changes.red_cards ?? red,
      started: changes.started ?? started,
    });
  };
  return (
    <div
      className={cn(
        // Mobile: card a 2 righe (info+pulsanti sopra, stepper sotto).
        // Desktop (sm+): grid una sola riga come prima.
        "flex flex-col gap-2 px-2 py-2.5 lg:grid lg:grid-cols-[28px_minmax(0,1fr)_36px_36px_repeat(4,92px)] lg:items-center lg:py-1.5",
              )}
    >
      {/* Blocco 1: avatar + nome + pulsanti C/T.
          sm:contents fa "scomparire" il wrapper nella grid del genitore
          su desktop, mantenendo gli stessi 4 elementi inline. */}
      <div className="flex items-center gap-2 min-w-0 lg:contents">
        {/* Ruolo in rosa (non quello giocato in partita), colorato per reparto. */}
        <Avatar className="h-7 w-7 shrink-0" title={playerRoleLabel(player.role_code) || undefined}>
          <AvatarFallback className="bg-raised text-[11px] font-semibold tracking-tight text-muted-foreground">
            {(player.role_code || "—").toUpperCase().slice(0, 3)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1 lg:flex-none">
          <p className="font-medium truncate leading-tight">
            {player.display_name}
            {player.is_guest && (
              <span className="ml-1.5 text-[13px] font-normal text-[hsl(var(--warn))]">
                {t("matchPanel.guestTag")}
              </span>
            )}
          </p>
          <p className="text-[13px] text-[hsl(var(--faint))] leading-tight min-h-[1em]">
            {/* Il ruolo sta nel cerchio: qui solo convocato/titolare, senza il
                separatore iniziale dei suffissi. */}
            {`${isCallup ? t("matchPanel.calledUpSuffix") : ""}${started ? t("matchPanel.starterSuffix") : ""}`.replace(/^\s*·\s*/, "")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onToggleCallup?.(!isCallup)}
          disabled={!onToggleCallup}
          className={cn(
            "h-11 w-11 lg:h-9 lg:w-9 shrink-0 rounded-full text-[13px] font-semibold transition-colors",
            isCallup ? "bg-accent text-accent-foreground" : "bg-raised text-muted-foreground hover:bg-[hsl(var(--active))]",
            !onToggleCallup && "opacity-40 cursor-not-allowed"
          )}
          title={isCallup ? t("matchPanel.toggleCallupOff") : t("matchPanel.toggleCallupOn")}
        >
          {t("matchPanel.thCallup")}
        </button>
        <button
          type="button"
          onClick={() => apply({ started: !started })}
          className={cn(
            "h-11 w-11 lg:h-9 lg:w-9 shrink-0 rounded-full text-[13px] font-semibold transition-colors",
            started ? "bg-primary text-primary-foreground" : "bg-raised text-muted-foreground hover:bg-[hsl(var(--active))]"
          )}
          title={started ? t("matchPanel.toggleStartOff") : t("matchPanel.toggleStartOn")}
        >
          {t("matchPanel.thStarter")}
        </button>
      </div>
      {/* Blocco 2: 4 stepper. Su mobile grid 4 colonne con label sopra,
          su sm+ contents -> finiscono nelle 4 colonne grid del parent. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2 lg:contents">
        <Stepper label={t("matchPanel.thGoals")} tone="emerald" value={goals} onChange={(v) => apply({ goals: v })} />
        <Stepper label={t("matchPanel.thAssists")} value={assists} onChange={(v) => apply({ assists: v })} />
        <Stepper label={t("matchPanel.thYellow")} tone="yellow" value={yellow} onChange={(v) => apply({ yellow_cards: v })} />
        <Stepper label={t("matchPanel.thRed")} tone="rose" value={red} onChange={(v) => apply({ red_cards: v })} />
      </div>
    </div>
  );
}

function Stepper({
  label,
  value,
  onChange,
  tone = "neutral",
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
  tone?: "emerald" | "amber" | "yellow" | "rose" | "neutral";
}) {
  const valueClass = {
    emerald: "text-[hsl(var(--ok))]",
    amber: "text-[hsl(var(--warn))]",
    yellow: "text-[hsl(var(--warn))]",
    rose: "text-[hsl(var(--danger))]",
    neutral: "text-foreground",
  }[tone];
  return (
    <div className="flex flex-col items-center gap-1 w-full">
      {/* Etichetta solo fino a lg: da li' in poi c'e' l'intestazione della tabella. */}
      <span className="lg:hidden text-[13px] text-[hsl(var(--faint))]">{label}</span>
      <div className="flex items-center justify-between rounded-full bg-raised h-11 lg:h-9 w-full">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 1))}
          className="w-11 lg:w-7 h-full rounded-full text-lg lg:text-sm text-muted-foreground hover:bg-[hsl(var(--active))] disabled:opacity-30"
          disabled={value <= 0}
          aria-label={`-1 ${label}`}
        >
          −
        </button>
        <span className={cn("eb-num text-[22px] pr-0.5", value > 0 ? valueClass : "text-[hsl(var(--faint))]")}>{value}</span>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="w-11 lg:w-7 h-full rounded-full text-lg lg:text-sm text-muted-foreground hover:bg-[hsl(var(--active))]"
          aria-label={`+1 ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}
